import type { MailLinkDto } from "../../../services/mailLinkService";

/**
 * Reine, testbare Helfer des Schriftwechsel-Reiters.
 *
 * A4 (Review 2026-07-06): Eine zugewiesene Mail (`MailLink`) wird **immer als
 * einzelne Nachricht** angezeigt — kein Konversations-Fan-out, kein „aus
 * Verlauf"-Untereintrag. Aus den zugewiesenen Links baut `buildAssignedMessages`
 * je Link genau EINE Zeile; das Öffnen erfolgt robust über die gespeicherte
 * die stabile Link-ID (Detailabruf im Fenster, s. `openLinkedMailWindow`).
 */

export type MailThreadMessage = {
    /** Stabiler React-Key — die Mail-Identität, siehe {@link mailIdentitaet}. */
    key: string;
    messageId: string;
    mailbox: string;
    subject: string;
    senderName: string;
    senderEmail: string;
    /** Null bei fehlendem/ungültigem Datum (rehydrierte Alt-Objekte). */
    date: Date | null;
    preview: string;
    /** Zugrunde liegender Zuweisungs-Datensatz (Quelle fürs Öffnen). */
    link: MailLinkDto;
    /**
     * An welchen To-dos DIESES Vorgangs die Mail ebenfalls hängt (PO 31.08.2026).
     *
     * Dieselbe Nachricht kann mehrere `MailLink`-Zeilen haben — eine am Vorgang,
     * je eine an den To-dos, an die sie gezogen wurde. Die Liste zeigt sie
     * trotzdem EINMAL; hier stehen die To-dos, damit die Zeile sagen kann, wohin
     * sie noch gehört, statt sie mehrfach anzuzeigen.
     */
    todoIds: number[];
};

const toDate = (v: unknown): Date | null => {
    if (v instanceof Date) return Number.isNaN(v.getTime()) || v.getTime() === 0 ? null : v;
    if (typeof v === "string" && v) {
        const d = new Date(v);
        return Number.isNaN(d.getTime()) || d.getTime() === 0 ? null : d;
    }
    return null;
};

/** Leerer/whitespace-Text zählt als „nicht vorhanden". */
const gesetzt = (s: string | null | undefined): string | null => {
    const t = (s ?? "").trim();
    return t.length > 0 ? t : null;
};

/**
 * Identität EINER MAIL — nicht die eines Exchange-Items.
 *
 * `messageId` ist die Graph-`id`: Dieselbe Nachricht trägt in „Gesendete
 * Elemente" eine andere als die zugestellte Kopie, und beim Verschieben ändert
 * sie sich erneut. Wer einen Verlauf anhängt, bekommt vom Server bewusst ALLE
 * Ordner des Postfachs („damit auch die eigenen Antworten aus ‚Gesendet' im
 * Thread erscheinen") — beide Kopien standen so als zwei Zeilen im
 * Schriftwechsel. Gemessen auf PROD am 15.09.2026: rund 13 % der Vorgangs-Mails
 * waren davon betroffen.
 *
 * Die `internetMessageId` (RFC-Message-Id) ist bei beiden Kopien identisch und
 * deshalb der richtige Schlüssel. Fehlt sie (Bestandszeilen, Lesepfade ohne das
 * Feld), bleibt es beim alten Verhalten: Postfach + Graph-Id. Das Präfix trennt
 * die Namensräume, damit eine Graph-Id nie zufällig gegen eine Internet-Id
 * trifft.
 */
export function mailIdentitaet(l: {
    mailbox: string;
    messageId: string;
    internetMessageId?: string | null;
}): string {
    const imid = gesetzt(l.internetMessageId);
    return imid ? `imid::${imid}` : `item::${l.mailbox}::${l.messageId}`;
}

/**
 * ALLE Schlüssel, unter denen dieser Link dieselbe Mail bezeichnet — die
 * RFC-Identität (falls vorhanden) UND immer die Item-Identität.
 *
 * Bewusst eine MENGE statt eines einzelnen Schlüssels: Es gibt Bestandszeilen
 * ohne `internetMessageId` neben neuen Zeilen mit ihr, und beide können auf
 * DASSELBE Exchange-Item zeigen (typisch: Vorgangs-Link von früher, Todo-Link
 * von heute). Mit nur einem Schlüssel je Link fielen die auseinander — das
 * alte Verhalten wäre schlechter geworden statt besser. So ist die frühere
 * Entdopplung die Untergrenze und die RFC-Id kommt additiv obendrauf.
 *
 * Der erste Eintrag ist der stärkste und dient als React-Key.
 */
function identitaetsSchluessel(l: {
    mailbox: string;
    messageId: string;
    internetMessageId?: string | null;
}): string[] {
    const item = `item::${l.mailbox}::${l.messageId}`;
    const imid = gesetzt(l.internetMessageId);
    return imid ? [`imid::${imid}`, item] : [item];
}

/**
 * Alle Schlüssel, unter denen die bereits zugewiesenen Mails wiederzuerkennen
 * sind — für den Picker, der eine schon verknüpfte Nachricht nicht ein zweites
 * Mal anbieten soll.
 *
 * Bewusst BEIDE Formen je Link: die Graph-`id` (so kommt eine Nachricht aus der
 * Mailliste) UND die `internetMessageId` (so wird die zweite Ordnerkopie
 * derselben Mail erkannt). Der Aufrufer prüft deshalb beides.
 */
export function zuweisungsSchluessel(
    links: readonly { mailbox: string; messageId: string; internetMessageId?: string | null }[],
): Set<string> {
    const out = new Set<string>();
    for (const l of links ?? []) {
        if (l.messageId) out.add(l.messageId);
        const imid = gesetzt(l.internetMessageId);
        if (imid) out.add(imid);
    }
    return out;
}

const msgFromLink = (l: MailLinkDto): MailThreadMessage => ({
    key: mailIdentitaet(l),
    messageId: l.messageId,
    mailbox: l.mailbox,
    subject: l.subject ?? "",
    senderName: l.senderName ?? "",
    senderEmail: l.senderEmail ?? "",
    date: toDate(l.receivedAt),
    preview: l.preview ?? "",
    link: l,
    todoIds: l.todoId != null ? [l.todoId] : [],
});

/**
 * Bildet die zugewiesenen Mails auf eine flache Liste einzelner Nachrichten ab
 * (eine Zeile je `MailLink`). Doppelte Zuweisungen DERSELBEN MAIL werden
 * entfernt — der Schlüssel ist {@link mailIdentitaet} und erkennt damit auch
 * die zweite Ordnerkopie („Gesendete Elemente" neben der zugestellten); die
 * Eingabereihenfolge bleibt stabil erhalten. Bewusst KEIN Zusammenfassen zu Konversations-Threads (A4).
 */
export function buildAssignedMessages(links: MailLinkDto[]): MailThreadMessage[] {
    const nachKey = new Map<string, MailThreadMessage>();
    const reihenfolge: MailThreadMessage[] = [];
    for (const l of links ?? []) {
        const keys = identitaetsSchluessel(l);
        const vorhanden = keys.map((k) => nachKey.get(k)).find(Boolean);
        let ziel: MailThreadMessage;
        if (vorhanden) {
            ziel = vorhanden;
        } else {
            ziel = msgFromLink(l);
            reihenfolge.push(ziel);
        }
        // JEDEN Schlüssel dieses Links auf den Eintrag zeigen lassen, nicht nur
        // den stärksten: Ein späterer Link, der nur die andere Form kennt, muss
        // ihn wiederfinden. Sonst zerfiele ein Paar aus Bestandszeile (ohne
        // RFC-Id) und neuer Zeile (mit RFC-Id) auf DASSELBE Exchange-Item in
        // zwei Zeilen — obwohl es vorher eine war.
        for (const k of keys) nachKey.set(k, ziel);
        // Zweiter Link auf dieselbe Nachricht: die Zeile bleibt EINE, aber sie
        // erbt die To-do-Zugehörigkeit. Sonst stünde die Mail zweimal da, sobald
        // jemand sie zusätzlich an ein To-do gehängt hat (PO 31.08.2026).
        if (l.todoId != null && !ziel.todoIds.includes(l.todoId)) {
            ziel.todoIds.push(l.todoId);
        }
    }
    return reihenfolge;
}

/**
 * Die Mail-Links eines Vorgangs, zu Gruppen DERSELBEN MAIL im SELBEN STRANG
 * zusammengefasst (T-11, 28.09.2026).
 *
 * Warum: Hängt derselbe Thread aus zwei Postfächern am Vorgang (etwa `dc@` und
 * `cll@`, beide Empfänger), verknüpft der Auto-Link jede neue Antwort JE
 * POSTFACH — gewollt, damit jeder in seiner eigenen Mailliste den
 * Vorgangs-Chip behält. Im Vorgang soll dieselbe Mail aber EINMAL stehen
 * (PO-Regel „dieselbe Mail steht im Schriftwechsel einmal“, siehe oben).
 *
 * Schlüssel ist {@link identitaetsSchluessel} (RFC-Id postfachübergreifend,
 * sonst Postfach + Graph-Id) PLUS das To-do: Eine Mail am Vorgang UND an einem
 * To-do fällt bewusst NICHT zusammen — das ist eine andere Frage (Invariante
 * „mit To-dos gehört alles einem To-do“) und nicht Teil dieser Entdopplung.
 *
 * Vertreter einer Gruppe ist der Link mit der KLEINSTEN Id — stabil, auch wenn
 * später weitere Kopien dazukommen. Er steht an der Stelle, an der die Gruppe
 * in der Eingabe zuerst auftaucht; die Reihenfolge bleibt so erhalten.
 *
 * - `sichtbar`: je Gruppe der Vertreter.
 * - `gruppeVon`: jede Id → alle Ids ihrer Gruppe (aufsteigend).
 * - `vertreterVon`: jede Id → Id des Vertreters (der Vertreter auf sich selbst).
 *
 * Reine Ableitung — gespeichert bleibt je Postfach.
 */
/**
 * Rückfall-Kennung für Bestandszeilen OHNE RFC-Id (Rücksprache 06.10.2026):
 * Absender + Betreff + Eingangsminute.
 *
 * Gemessen auf PROD am 06.10.2026: 158 Paare derselben Mail im selben Strang,
 * bei denen eine Kopie die `internetMessageId` trug und die andere (vor dem
 * 15.09.2026 verknüpft) nicht — `identitaetsSchluessel` konnte sie nicht
 * zusammenführen, und dieselbe Mail stand zweimal da. Absender, Betreff und
 * Minute sind bei beiden Postfachkopien identisch.
 *
 * Die Kennung führt nur zusammen, wo GENAU EINE Seite eine RFC-Id trägt —
 * der gemessene Fall. Zwei Mails mit VERSCHIEDENEN RFC-Ids sind nie dieselbe,
 * auch wenn sie in derselben Minute denselben Betreff tragen (Serienmail an
 * zwei Empfänger); zwei Zeilen ganz ohne bleiben beim bisherigen Schlüssel.
 */
function rueckfallKennung(l: {
    senderEmail?: string | null;
    subject?: string | null;
    receivedAt?: string | null;
}): string | null {
    const absender = gesetzt(l.senderEmail)?.toLowerCase();
    const betreff = gesetzt(l.subject);
    const t = l.receivedAt ? new Date(l.receivedAt).getTime() : NaN;
    if (!absender || !betreff || !Number.isFinite(t) || t <= 0) return null;
    return `fp::${absender}|${betreff}|${Math.floor(t / 60_000)}`;
}

export interface VorgangsMailGruppen {
    sichtbar: MailLinkDto[];
    gruppeVon: Map<number, number[]>;
    vertreterVon: Map<number, number>;
}

export function gruppiereVorgangsMails(links: readonly MailLinkDto[]): VorgangsMailGruppen {
    /** `rfc`: die RFC-Id der Gruppe, sobald eine ihrer Zeilen eine trägt. */
    type Gruppe = { ids: number[]; vertreter: MailLinkDto; rfc: string | null };
    const nachKey = new Map<string, Gruppe>();
    /** Rückfall-Kennung → Gruppen (mehrere möglich: Serienmail mit zwei RFC-Ids). */
    const nachKennung = new Map<string, Gruppe[]>();
    const gruppen: Gruppe[] = [];
    for (const l of links ?? []) {
        const strang = l.todoId != null ? `t${l.todoId}` : "v";
        const keys = identitaetsSchluessel(l).map((k) => `${strang}|${k}`);
        const rfc = gesetzt(l.internetMessageId);
        const kennung = rueckfallKennung(l);
        const kennungKey = kennung ? `${strang}|${kennung}` : null;
        // Über die Kennung nur eine Zeile OHNE RFC-Id mit einer Gruppe MIT —
        // zwei Mails mit verschiedenen RFC-Ids sind nie dieselbe (siehe
        // `rueckfallKennung`).
        let gruppe =
            keys.map((k) => nachKey.get(k)).find(Boolean) ??
            (kennungKey
                ? (nachKennung.get(kennungKey) ?? []).find(
                      // GENAU eine Seite trägt eine RFC-Id: Zwei Zeilen ohne
                      // bleiben beim bisherigen Schlüssel (Postfach + Graph-Id).
                      (g) => (!rfc || !g.rfc) && (!!rfc || !!g.rfc),
                  )
                : undefined);
        if (!gruppe) {
            gruppe = { ids: [], vertreter: l, rfc: null };
            gruppen.push(gruppe);
        } else if (l.id < gruppe.vertreter.id) {
            gruppe.vertreter = l;
        }
        if (rfc && !gruppe.rfc) gruppe.rfc = rfc;
        if (!gruppe.ids.includes(l.id)) gruppe.ids.push(l.id);
        // Alle Schlüssel zeigen auf die Gruppe — wie in `buildAssignedMessages`,
        // damit Bestandszeile (ohne RFC-Id) und neue Zeile zusammenfinden.
        for (const k of keys) nachKey.set(k, gruppe);
        if (kennungKey) {
            const liste = nachKennung.get(kennungKey) ?? [];
            if (!liste.includes(gruppe)) liste.push(gruppe);
            nachKennung.set(kennungKey, liste);
        }
    }
    const sichtbar: MailLinkDto[] = [];
    const gruppeVon = new Map<number, number[]>();
    const vertreterVon = new Map<number, number>();
    for (const g of gruppen) {
        sichtbar.push(g.vertreter);
        const ids = [...g.ids].sort((a, b) => a - b);
        for (const lid of ids) {
            gruppeVon.set(lid, ids);
            vertreterVon.set(lid, g.vertreter.id);
        }
    }
    return { sichtbar, gruppeVon, vertreterVon };
}

/**
 * Alle Link-Ids, die eine sichtbare Mail-Karte vertritt — was „Aus dem Vorgang
 * entfernen“ bzw. „einem To-do zuweisen“ anfassen muss. Sonst käme die
 * ausgeblendete Postfachkopie nach dem Neuladen als eigene Karte zurück
 * (entfernt) bzw. bliebe im alten Strang stehen (verschoben).
 */
export function mailLinksDerGruppe(mailLinkId: number, gruppeVon: ReadonlyMap<number, readonly number[]>): number[] {
    const gruppe = gruppeVon.get(mailLinkId);
    return gruppe && gruppe.length > 0 ? [...gruppe] : [mailLinkId];
}
